/**
 * Appels API pour les contacts.
 */

import api from "./axiosConfig";

/**
 * Récupère la liste des contacts visibles.
 * @param {string} sortBy - Colonne de tri ("last_name" ou "company").
 * @param {string} [company] - Filtre partiel sur le nom de société.
 * @returns {Promise<Array>} Liste des contacts.
 */
export async function getContacts(sortBy = "last_name", company = "") {
  const params = new URLSearchParams({ sort_by: sortBy });
  if (company) params.set("company", company);
  const response = await api.get(`/contacts?${params.toString()}`);
  return response.data;
}

/**
 * Récupère un contact par son ID.
 * @param {number} id - Identifiant du contact.
 * @returns {Promise<Object>} Le contact.
 */
export async function getContact(id) {
  const response = await api.get(`/contacts/${id}`);
  return response.data;
}

/**
 * Crée un nouveau contact.
 * @param {Object} data - Données du contact.
 * @returns {Promise<Object>} Le contact créé.
 */
export async function createContact(data) {
  const response = await api.post("/contacts", data);
  return response.data;
}

/**
 * Met à jour un contact existant.
 * @param {number} id - Identifiant du contact.
 * @param {Object} data - Champs à modifier.
 * @returns {Promise<Object>} Le contact mis à jour.
 */
export async function updateContact(id, data) {
  const response = await api.put(`/contacts/${id}`, data);
  return response.data;
}

/**
 * Supprime un contact.
 * @param {number} id - Identifiant du contact.
 * @returns {Promise<void>}
 */
export async function deleteContact(id) {
  await api.delete(`/contacts/${id}`);
}

/**
 * Récupère la timeline d'un contact.
 * @param {number} id - Identifiant du contact.
 * @returns {Promise<Array>} Liste des événements triés par date.
 */
export async function getContactTimeline(id) {
  const response = await api.get(`/contacts/${id}/timeline`);
  return response.data;
}

/**
 * Récupère tous les To Dos rattachés à un contact (tous utilisateurs).
 * @param {number} id - Identifiant du contact.
 * @returns {Promise<Array>} Liste des todos.
 */
export async function getContactTodos(id) {
  const response = await api.get(`/contacts/${id}/todos`);
  return response.data;
}

/**
 * Récupère toutes les actions spontanées rattachées à un contact (tous utilisateurs).
 * @param {number} id - Identifiant du contact.
 * @returns {Promise<Array>} Liste des actions.
 */
export async function getContactActions(id) {
  const response = await api.get(`/contacts/${id}/spontaneous-actions`);
  return response.data;
}
